/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { DOCUMENT } from '@angular/common'
import {
  Component,
  OnInit,
  afterNextRender,
  inject,
  signal,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core'
import { Meta, Title } from '@angular/platform-browser'
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router'
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker'
import { TranslateService } from '@ngx-translate/core'
import { filter, map } from 'rxjs'
import { HeaderComponent } from './modules/sistema/header/header.component'
import { ToasterComponent } from './modules/toast/components/toaster/toaster.component'
import { ModalComponent } from './modules/formulario/file-preview-modal/components/modal.component'
import { BoletoModalComponent } from './modules/candidato/boleto-preview-modal/components/boleto-modal.component'
import { PixModalComponent } from './modules/candidato/pix-preview-modal/components/pix-modal.component'
import { LoaderComponent } from './modules/loader/components/loader.component'
import { FooterComponent } from './modules/sistema/footer/footer.component'
import { StorageService } from './services/storage.service'
import { LoaderService } from './modules/loader/services/loader.service'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  imports: [
    HeaderComponent,
    ToasterComponent,
    ModalComponent,
    BoletoModalComponent,
    PixModalComponent,
    LoaderComponent,
    RouterOutlet,
    FooterComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private swUpdate = inject(SwUpdate)
  private cdr = inject(ChangeDetectorRef)
  private readonly translate = inject(TranslateService)
  private readonly titleService = inject(Title)
  private readonly meta = inject(Meta)
  private readonly storage = inject(StorageService)
  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly document = inject(DOCUMENT)
  protected readonly loaderService = inject(LoaderService)

  private readonly baseTitle = 'UNICAMP :: Concursos'

  title: string = 'Sistema de inscrições'
  showToTop = signal(false)

  constructor() {
    const savedLang = this.storage.getLanguage()
    this.translate.setDefaultLang('pt')
    this.translate.reloadLang(savedLang).subscribe(() => {
      this.translate.use(savedLang)
      this.document.documentElement.lang = savedLang
    })

    afterNextRender(() => {
      import('bootstrap')
      window.addEventListener(
        'scroll',
        () => {
          this.showToTop.set(window.scrollY > 20)
        },
        { passive: true }
      )
    })
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  ngOnInit(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe(() => {
          if (confirm('Nova versão do sistema disponível. Deseja atualizar?')) {
            document.location.reload()
          }
        })
    }

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          let child = this.route
          while (child.firstChild) child = child.firstChild
          return child.snapshot.data
        })
      )
      .subscribe(data => {
        const fullTitle = data?.['title'] ? data['title'] + ' | ' + this.baseTitle : this.baseTitle
        this.titleService.setTitle(fullTitle)
        this.meta.updateTag({ property: 'og:title', content: fullTitle })
        this.meta.updateTag({ name: 'twitter:title', content: fullTitle })
        if (data?.['description']) {
          this.meta.updateTag({ name: 'description', content: data['description'] })
          this.meta.updateTag({ property: 'og:description', content: data['description'] })
          this.meta.updateTag({ name: 'twitter:description', content: data['description'] })
        }
        if (data?.['noindex']) {
          this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' })
        } else {
          this.meta.removeTag("name='robots'")
        }
        this.updateCanonical()
        document.getElementById('conteudo-principal')?.focus({ preventScroll: true })
      })
  }

  private updateCanonical(): void {
    const url = this.document.location.origin + this.router.url.split('?')[0]
    this.meta.updateTag({ property: 'og:url', content: url })
    let link = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement
    if (!link) {
      link = this.document.createElement('link')
      link.setAttribute('rel', 'canonical')
      this.document.head.appendChild(link)
    }
    link.setAttribute('href', url)
  }
}
